import type {
  ApprovalStatus,
  CurrentUser,
  Plan,
  Message,
  Paginated,
  Participant,
  ReceiveMode,
  Room,
  RoomFilter,
} from "@catmail/shared";

/**
 * 서버와 이야기하는 얇은 층.
 *
 * 개발 중에도 프론트만 로컬에서 띄우고 백엔드는 공용 서버를 쓴다. Vite 프록시가
 * `/api`를 넘겨주므로 브라우저 입장에서는 언제나 동일 출처다 — 그래서 절대 URL을
 * 쓰지 않고, CORS와 쿠키 SameSite 문제도 생기지 않는다.
 */

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    // 세션 쿠키가 붙어야 한다.
    credentials: "same-origin",
    headers: {
      ...(init.body ? { "content-type": "application/json" } : {}),
      ...init.headers,
    },
  });

  if (!response.ok) {
    const detail = (await response.json().catch(() => null)) as {
      error?: string;
    } | null;
    throw new ApiError(
      response.status,
      detail?.error ?? `요청이 실패했습니다 (${response.status})`,
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

// --- 인증 -------------------------------------------------------------------

export type MeResponse = Pick<
  CurrentUser,
  | "id"
  | "displayName"
  | "primaryAddress"
  | "receiveMode"
  | "addresses"
  | "avatarUrl"
> & {
  isAdmin: boolean;
  plan: Plan;
  /**
   * 옛 API는 이 값을 안 보낸다. **없는 것과 pending은 다르다** —
   * 없다고 대기로 치면 멀쩡한 사람이 승인 화면에 갇힌다.
   */
  status?: ApprovalStatus;
};

export type PendingUser = {
  id: string;
  displayName: string;
  address: string;
  /** 신청한 주소의 identity id. 고쳐서 받아줄 때 쓴다. 없으면 고칠 것이 없다 */
  addressId: string | null;
  createdAt: string;
};

export type PendingIdentity = {
  id: string;
  address: string;
  createdAt: string;
  owner: { id: string; displayName: string; plan: Plan };
  /** 이 사람이 지금 열어둔 주소 수. 상한이 아니라 사실이다 — 판단에 필요한 것 */
  owned: number;
};

export type SignupInput = {
  /** 전체 주소. 서버가 `@<도메인>`으로 끝나는지 확인한다 */
  address: string;
  displayName: string;
  password: string;
};

export const authApi = {
  /** 로그인 전에도 부를 수 있는 값. 가입 화면이 주소 도메인을 여기서 받는다 */
  config: () => request<{ mailDomain: string }>("/api/config"),

  me: () => request<MeResponse>("/api/auth/me"),

  /** 성공하면 세션 쿠키까지 붙어서 온다 — 따로 로그인하지 않는다 */
  signup: (input: SignupInput) =>
    request<{ ok: true }>("/api/auth/signup", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  login: (address: string, password: string) =>
    request<{ ok: true }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ address, password }),
    }),

  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  updateProfile: (input: { displayName?: string; receiveMode?: ReceiveMode }) =>
    request<{ displayName: string; receiveMode: ReceiveMode }>("/api/auth/me", {
      method: "PATCH",
      body: JSON.stringify(input),
    }),

  changePassword: (input: { current: string; next: string }) =>
    request<{ ok: true; signedOutSessions: number }>("/api/auth/password", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  /**
   * 프로필 사진을 올린다. 이미 **정사각형으로 줄여서** 오는 base64다
   * (lib/avatarImage.ts). 서버는 크기와 형식만 다시 확인한다.
   */
  uploadAvatar: (contentBase64: string) =>
    request<{ avatarUrl: string }>("/api/auth/me/avatar", {
      method: "POST",
      body: JSON.stringify({ contentBase64 }),
    }),

  removeAvatar: () =>
    request<{ ok: true }>("/api/auth/me/avatar", { method: "DELETE" }),
};

// --- 방 --------------------------------------------------------------------

export type RoomCounts = Record<RoomFilter, number> & {
  /**
   * 주소별 안 읽음. 지금 고른 주소로 좁혀도 **이건 늘 전부** 온다 — 갈아타는
   * 자리에 다른 주소의 숫자가 있어야 거기 쌓인 것을 눈치챈다.
   *
   * 옛 API는 안 보낸다. 없으면 숫자를 안 그린다.
   */
  byIdentity?: Record<string, number>;
};

export const roomsApi = {
  list: (input: {
    filter: RoomFilter;
    query: string;
    cursor?: string | null;
    /** 고른 주소. 없으면 내 주소 전부가 섞여 온다 */
    identityId?: string | null;
  }) => {
    const params = new URLSearchParams({ filter: input.filter });
    if (input.query.trim()) params.set("q", input.query.trim());
    if (input.cursor) params.set("cursor", input.cursor);
    if (input.identityId) params.set("identityId", input.identityId);

    return request<Paginated<Room>>(`/api/rooms?${params.toString()}`);
  },

  /**
   * 말 걸기 전에 방을 연다. **메일은 나가지 않는다** — 상대는 첫 메일이 가야
   * 이 방을 안다. 같은 사람으로 다시 열면 하던 방이 그대로 온다.
   */
  create: (input: { to: string[]; identityId?: string | null }) =>
    request<Room>("/api/rooms", {
      method: "POST",
      body: JSON.stringify({
        to: input.to,
        ...(input.identityId ? { identityId: input.identityId } : {}),
      }),
    }),

  counts: (identityId?: string | null) =>
    request<RoomCounts>(
      identityId
        ? `/api/rooms/counts?identityId=${encodeURIComponent(identityId)}`
        : "/api/rooms/counts",
    ),

  /**
   * 방 하나. 링크로 받은 방이 지금 불러온 목록에 없을 때만 부른다 —
   * 목록은 페이지로 잘리고 필터로도 걸러지므로 멀쩡한 방이 안 보일 수 있다.
   */
  get: (roomId: string) => request<Room>(`/api/rooms/${roomId}`),

  messages: (roomId: string, cursor?: string | null) => {
    const params = new URLSearchParams();
    if (cursor) params.set("cursor", cursor);
    const query = params.toString();

    return request<Paginated<Message>>(
      `/api/rooms/${roomId}/messages${query ? `?${query}` : ""}`,
    );
  },

  markRead: (roomId: string) =>
    request<{ ok: true }>(`/api/rooms/${roomId}/read`, { method: "POST" }),

  setQuiet: (roomId: string, quiet: boolean) =>
    request<{ ok: true; isQuiet: boolean }>(`/api/rooms/${roomId}/quiet`, {
      method: "POST",
      body: JSON.stringify({ quiet }),
    }),

  readAllQuiet: () =>
    request<{ ok: true; changed: number }>("/api/rooms/quiet/read-all", {
      method: "POST",
    }),
};

// --- 보내기 -----------------------------------------------------------------

export const messagesApi = {
  send: (input: {
    roomId?: string;
    /**
     * 어느 주소로 보낼지. **새 대화에서만 쓴다.**
     *
     * 답장은 서버가 원본이 들어온 주소를 그대로 쓴다(architecture.md §4.7).
     * 스레드가 아니라 그 메시지가 온 주소가 기준이라, 여기서 지금 고른 계정을
     * 밀어 넣으면 `netflix@`로 온 메일에 `yena@`로 답하게 된다.
     */
    identityId?: string;
    to?: string[];
    cc?: string[];
    body: string;
    subject?: string;
    attachments?: Array<{
      filename: string;
      mimeType: string;
      contentBase64: string;
    }>;
  }) =>
    request<{ roomId: string; message: Message | null }>("/api/messages", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  /** 원문 HTML은 열 때만 가져온다. 광고 메일 한 통이 수백 KB다. */
  html: (messageId: string) =>
    request<{ html: string }>(`/api/messages/${messageId}/html`),

  /** 못 보낸 메일을 다시 큐에 넣는다. 같은 행을 되돌리므로 말이 두 번 남지 않는다. */
  retry: (messageId: string) =>
    request<{ message: Message | null }>(`/api/messages/${messageId}/retry`, {
      method: "POST",
    }),
};

export const identitiesApi = {
  /**
   * 주소를 **신청한다.** 만드는 것이 아니다 — 관리자가 받아줘야 메일이 들어온다.
   *
   * 도메인은 서버가 붙인다. 하나뿐이라 고를 것이 없고, 화면에서 붙여 보내면
   * 도메인이 바뀌는 날 화면도 같이 고쳐야 한다.
   */
  request: (localPart: string) =>
    request<{ id: string; address: string; status: ApprovalStatus }>(
      "/api/identities",
      { method: "POST", body: JSON.stringify({ localPart }) },
    ),

  /**
   * 주소를 껐다 켠다. **지우는 것이 아니다** — 껐다 켜면 대화가 그대로 돌아온다.
   * 끄면 그 주소로 오는 메일을 엣지가 거절한다.
   */
  setEnabled: (identityId: string, enabled: boolean) =>
    request<{ ok: true }>(`/api/identities/${identityId}`, {
      method: "PATCH",
      body: JSON.stringify({ enabled }),
    }),

  /** 주소를 지운다. **그 주소로 온 대화도 함께 사라진다**(연쇄 삭제). */
  remove: (identityId: string) =>
    request<{ ok: true }>(`/api/identities/${identityId}`, { method: "DELETE" }),
};

/** 최고 관리자만 부를 수 있다. 아니면 404가 온다 — 있다는 것도 알리지 않는다. */
export const adminApi = {
  pending: () =>
    request<{ users: PendingUser[]; identities: PendingIdentity[] }>(
      "/api/admin/pending",
    ),

  approveUser: (userId: string, plan?: Plan) =>
    request<{ ok: true; opened: number }>(`/api/admin/users/${userId}/approve`, {
      method: "POST",
      body: JSON.stringify(plan ? { plan } : {}),
    }),

  rejectUser: (userId: string) =>
    request<{ ok: true }>(`/api/admin/users/${userId}/reject`, { method: "POST" }),

  /**
   * 신청된 주소를 **고쳐서** 받아준다. 오타 하나로 거절하면 신청자는 무엇이
   * 잘못됐는지 모른 채 다시 신청한다.
   */
  changeAddress: (identityId: string, localPart: string) =>
    request<{ ok: true; address: string; previous: string; isPrimary: boolean }>(
      `/api/admin/identities/${identityId}/address`,
      { method: "PATCH", body: JSON.stringify({ localPart }) },
    ),

  approveIdentity: (identityId: string) =>
    request<{ ok: true }>(`/api/admin/identities/${identityId}/approve`, {
      method: "POST",
    }),

  rejectIdentity: (identityId: string) =>
    request<{ ok: true }>(`/api/admin/identities/${identityId}/reject`, {
      method: "POST",
    }),
};

export const contactsApi = {
  /**
   * 사람 찾기. **주소별로 상대 목록이 다르다** — `contacts`가 identity마다 따로
   * 쌓이기 때문이다(db/schema.ts). 그래서 고른 주소를 같이 넘긴다.
   */
  search: (query: string, identityId?: string | null) => {
    const params = new URLSearchParams();
    if (query.trim()) params.set("q", query.trim());
    if (identityId) params.set("identityId", identityId);
    const suffix = params.toString();

    return request<{ items: Participant[] }>(
      `/api/contacts${suffix ? `?${suffix}` : ""}`,
    );
  },

  /** `customName`에 빈 문자열을 넣으면 별칭을 지우고 원래 이름으로 돌아간다. */
  update: (contactId: string, input: { customName?: string; blocked?: boolean }) =>
    request<{
      id: string;
      address: string;
      customName: string | null;
      blocked: boolean;
    }>(`/api/contacts/${contactId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
};

// --- 알림 -------------------------------------------------------------------

/** 브라우저가 푸시 서비스에서 받아온 구독. 그대로 서버에 맡긴다 */
export type PushSubscriptionInput = {
  endpoint: string;
  keys: { p256dh: string; auth: string };
};

export const pushApi = {
  /**
   * 서버가 알림을 보낼 수 있는가와, 구독할 때 쓸 공개키.
   *
   * 키를 화면에 박아두지 않는 이유는 서버가 쓰는 키와 **같은 쌍**이어야 하기
   * 때문이다. 어긋나면 구독은 되는데 알림만 영영 안 온다.
   */
  key: () => request<{ enabled: boolean; publicKey: string | null }>("/api/push/key"),

  subscribe: (subscription: PushSubscriptionInput) =>
    request<{ ok: true }>("/api/push/subscriptions", {
      method: "POST",
      body: JSON.stringify(subscription),
    }),

  unsubscribe: (endpoint: string) =>
    request<{ ok: true }>("/api/push/subscriptions", {
      method: "DELETE",
      body: JSON.stringify({ endpoint }),
    }),
};
